// Which budget pricing strategy covers a given budget entry.
//
// A strategy is scoped by commodity, location and counterparty, and a null on any of
// those means "applies to all" - so covering an entry is a per-dimension wildcard test
// rather than an equality test. Budget entry, the importer and the settings screen all
// need the same answer, so the rule lives here rather than in a component.
//
// Note the null-on-the-ENTRY case: a budget with no location is only covered by a
// strategy that is not location-scoped. The version of this that lived inside
// AddBudgets treated an empty selection as matching anything, which let a strategy for
// one specific location silently price a budget with no location at all.
import { BudgetPricingStrategyModel } from '../Generated/Commodity-Management-Api';

/** The commodity/location/counterparty a budget entry is being created for. */
export interface PricingStrategyScope {
    commodityId?: number | null;
    locationId?: number | null;
    counterpartyId?: number | null;
}

function dimensionCovers(strategyValue: number | null | undefined, entryValue: number | null | undefined): boolean {
    // Unset on the strategy is a wildcard.
    if (strategyValue === null || strategyValue === undefined) return true;
    return strategyValue === entryValue;
}

/** Whether one strategy applies to an entry. */
export function pricingStrategyCovers(
    strategy: BudgetPricingStrategyModel,
    scope: PricingStrategyScope
): boolean {
    return dimensionCovers(strategy.commodityId, scope.commodityId)
        && dimensionCovers(strategy.locationId, scope.locationId)
        && dimensionCovers(strategy.counterpartyId, scope.counterpartyId);
}

/** How many dimensions a strategy pins down; higher is more specific. */
export function getPricingStrategySpecificity(strategy: BudgetPricingStrategyModel): number {
    return [strategy.commodityId, strategy.locationId, strategy.counterpartyId]
        .filter(value => value !== null && value !== undefined)
        .length;
}

/**
 * Every strategy covering an entry, most specific first. More than one can legitimately
 * apply (a commodity-wide strategy and a location-specific one), so this returns them
 * all and lets the caller decide - the budget screen asks the user which to use rather
 * than refusing to continue, which is what it used to do.
 */
export function findMatchingPricingStrategies(
    strategies: readonly BudgetPricingStrategyModel[] | undefined,
    scope: PricingStrategyScope
): BudgetPricingStrategyModel[] {
    return (strategies ?? [])
        .filter(strategy => pricingStrategyCovers(strategy, scope))
        .sort((a, b) =>
            getPricingStrategySpecificity(b) - getPricingStrategySpecificity(a)
            || (a.name ?? '').localeCompare(b.name ?? ''));
}

/**
 * A strategy with no components prices nothing: the budget grid builds one price column
 * per component, so budgets entered against it carry a volume and no price at all.
 * Treated as "not usable yet" wherever a strategy is required.
 */
export function hasPricingComponents(strategy: BudgetPricingStrategyModel | null | undefined): boolean {
    return (strategy?.components?.length ?? 0) > 0;
}
